"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherSnowflake,
  FeatherThermometer,
  FeatherToggleLeft,
  FeatherToggleRight,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import {
  useListColdStorageQueryControllerList,
  useUpdateColdStorageCommandControllerUpdate,
} from "@/generated/cold-storage/cold-storage";
import type { ColdStorageResponseDto } from "@/generated/model";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

import { ColdStorageFormDialog } from "./ColdStorageFormDialog";

interface ColdStorageTableProps {
  nurseryId: string;
}

export function ColdStorageTable({ nurseryId }: ColdStorageTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<ColdStorageResponseDto | null>(
    null,
  );

  const {
    data: unitsData,
    isLoading,
    error,
  } = useListColdStorageQueryControllerList(nurseryId, {
    includeInactive: true,
  });

  const units = useMemo(() => unitsData?.data ?? [], [unitsData]);

  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full set, so the page is sliced client-side.
  const pagedUnits = useMemo(
    () => units.slice((page - 1) * pageSize, page * pageSize),
    [units, page, pageSize],
  );

  const { mutate: toggleActive } = useUpdateColdStorageCommandControllerUpdate({
    mutation: {
      onSuccess: async () => {
        toast.success("Unit status updated");
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            query.queryKey[0].includes("/cold-storage"),
          refetchType: "all",
        });
      },
      onError: () => {
        toast.error("Failed to update unit status");
      },
    },
  });

  const handleCreateClick = () => {
    setEditingUnit(null);
    setDialogOpen(true);
  };

  const handleEditClick = (unit: ColdStorageResponseDto) => {
    setEditingUnit(unit);
    setDialogOpen(true);
  };

  const handleToggleActive = (unit: ColdStorageResponseDto) => {
    toggleActive({
      nurseryId,
      coldStorageId: unit.id,
      data: { isActive: !unit.isActive },
    });
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingUnit(null);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Fridges &amp; Freezers
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage cold-storage units for this nursery
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreateClick}
        >
          Add Unit
        </Button>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : error ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
          <IconWithLogoBackground variant="error" size="x-large" />
          <span className="text-body font-body text-subtext-color">
            Error loading cold-storage units. Please try again.
          </span>
        </div>
      ) : units.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
          <IconWithLogoBackground
            variant="neutral"
            size="x-large"
            icon={<FeatherSnowflake />}
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              No fridges or freezers yet
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              Add a fridge or freezer to track its target temperature.
            </span>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={handleCreateClick}
          >
            Add Unit
          </Button>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
          <Table
            className={TABLE_MIN_WIDTH.md}
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Room</Table.HeaderCell>
                <Table.HeaderCell>Target temp (°C)</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="items-center justify-end">
                  Actions
                </Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {pagedUnits.map((unit) => (
              <Table.Row key={unit.id}>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {unit.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    variant={unit.type === "FREEZER" ? "brand" : "neutral"}
                    icon={
                      unit.type === "FREEZER" ? (
                        <FeatherSnowflake />
                      ) : (
                        <FeatherThermometer />
                      )
                    }
                  >
                    {unit.type === "FREEZER" ? "Freezer" : "Fridge"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {unit.room.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {unit.targetTempMin} to {unit.targetTempMax}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {unit.isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="neutral">Inactive</Badge>
                  )}
                </Table.Cell>
                <Table.Cell variant="aligned-right">
                  <div className="flex items-center justify-end gap-2">
                    <SubframeCore.DropdownMenu.Root>
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <IconButton
                          variant="neutral-primary"
                          size="medium"
                          icon={<FeatherMoreHorizontal />}
                        />
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="end"
                          sideOffset={4}
                          asChild={true}
                        >
                          <DropdownMenu>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherPencil />}
                              onClick={() => handleEditClick(unit)}
                            >
                              Edit
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={
                                unit.isActive ? (
                                  <FeatherToggleLeft />
                                ) : (
                                  <FeatherToggleRight />
                                )
                              }
                              onClick={() => handleToggleActive(unit)}
                            >
                              {unit.isActive ? "Deactivate" : "Activate"}
                            </DropdownMenu.DropdownItem>
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>

          <Pagination
            className="pt-4"
            page={page}
            pageSize={pageSize}
            totalCount={units.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            itemNoun="units"
          />
        </div>
      )}

      <ColdStorageFormDialog
        nurseryId={nurseryId}
        unit={editingUnit}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
      />
    </div>
  );
}
